/* eslint-disable react/no-array-index-key */
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import { selectViewings, openViewings, ViewingsState } from '@propertypal/shared/src/reducers/viewings';
import OgImage from '@propertypal/shared/src/resources/images/open-viewings/open-graph-open-viewings.jpg';
import { AuctionDate } from '@propertypal/shared/src/types/auction';
import { ViewingSearchResult } from '@propertypal/shared/src/types/viewing';
import DateCalendar from '@propertypal/web-ui/src/calendar/DateCalendar';
import { Box, RowCenter } from '@propertypal/web-ui/src/layout';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import PropertyBox from '@propertypal/web-ui/src/property/PropertyBox';
import GridContainer from '@propertypal/web-ui/src/property/PropertyGrid.style';
import PropertyPlaceholder from '@propertypal/web-ui/src/property/PropertyPlaceholder';
import { useToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { Heading, Text } from '@propertypal/web-ui/src/typography';
import dayjs from 'dayjs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next/types';
import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import MetaTags from '../components/head/MetaTags';
import IMAGE_LOCATOR, { IMAGE_LOCATOR_LARGE } from '../constants/imageLocator';
import { requireAuthToken } from '../utils/auth';
import favouriteProperty from '../utils/favouriteProperty';

const META_TITLE = 'Open Viewings {LOCATION}{SALETYPE} - PropertyPal';
const META_DESCRIPTION = `
Theres no better way to get a feel for a house than seeing it.
With PropertyPal Open Viewings you'll be able to see if a home is a perfect fit in person.`;

export const getServerSideProps: GetServerSideProps = requireAuthToken(async (ctx, store) => {
  const getSaleType = () => {
    if (ctx?.query && ctx.query.saleType && typeof ctx.query.saleType === 'string') {
      const st = parseFloat(ctx.query.saleType);
      if (st === 1 || st === 2) return st;
    }
    return undefined;
  };

  const getTerm = () => {
    if (ctx?.query && ctx.query.term && typeof ctx.query.term === 'string' && !Number.isNaN(parseFloat(ctx.query.term)))
      return parseFloat(ctx.query.term);
    return undefined;
  };

  const getDate = () => {
    if (
      ctx?.query &&
      ctx.query.date &&
      typeof ctx.query.date === 'string' &&
      dayjs(ctx.query.date, 'YYYY-MM-DD').isValid()
    )
      return ctx.query.date;
    return undefined;
  };

  const saleType = getSaleType();
  const term = getTerm();
  const date = getDate();

  await store.dispatch(openViewings(date, saleType, term, IMAGE_LOCATOR, ctx));

  return {
    props: null,
  };
});

const getTown = (viewings: ViewingsState) => {
  if (viewings.term && viewings.data?.primaryTowns && viewings.data?.primaryTowns.length > 0) {
    const location = viewings.data?.primaryTowns.find((t) => t.id === viewings.term);

    if (location?.uniqueName) {
      return ` in ${location.uniqueName}`;
    }
  }

  return '';
};

const OpenViewings: FC = () => {
  const viewings = useSelector(selectViewings);
  const { openToast } = useToast();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const town = getTown(viewings);
  const metaTitle = META_TITLE.replace('{SALETYPE}', viewings.saleType === 2 ? ' (To Rent)' : ' (For Sale)').replace(
    '{LOCATION}',
    town,
  );

  const onFavourite = useCallback(() => {
    return favouriteProperty(dispatch);
  }, []);

  const updateDate = async (date: AuctionDate['value']) => {
    const res = await dispatch(openViewings(date, viewings.saleType, viewings.term, IMAGE_LOCATOR));
    if (!res)
      openToast({
        title: 'Error',
        message: "Something went wrong. We couldn't retrieve open viewings from selected date. Please try again!",
        type: 'error',
      });
  };

  const renderEmpty = () => (
    <Box ta="center" m="50px 0px" data-testid="empty-viewings" p="0px 12px">
      <Heading>No Viewings Scheduled</Heading>
      <Text mt={5} data-testid="empty-text">
        There are no open viewings{' '}
        {viewings.currentDate
          ? `scheduled for ${dayjs(viewings.currentDate, 'YYYY-MM-DD').format('dddd MMMM DD YYYY')}`
          : `currently scheduled`}
        .
      </Text>
    </Box>
  );

  const renderViewings = (results: ViewingSearchResult['results']) => (
    <RowCenter data-testid="viewing-results">
      <Box maxWidth={1260 + 30 * 2} m="0 auto">
        <GridContainer aria-label={`Open Viewings on ${viewings.currentDate}`} role="listbox" maxColumns={3}>
          {results.map((a, i) => (
            <PropertyBox
              key={i}
              property={a}
              showOpenViewingLabel
              onFavourite={onFavourite}
              imageLocator={IMAGE_LOCATOR_LARGE}
            />
          ))}
        </GridContainer>
      </Box>
    </RowCenter>
  );

  const renderLoading = () => (
    <RowCenter data-testid="loading-viewings">
      <Box maxWidth={1260 + 30 * 2} m="0 auto">
        <GridContainer aria-label={`Open Viewings on ${viewings.currentDate}`} role="listbox" maxColumns={3}>
          {Array.from({ length: 3 }).map((_, i) => (
            <PropertyPlaceholder key={i} />
          ))}
        </GridContainer>
      </Box>
    </RowCenter>
  );

  const renderContent = () => {
    if (viewings.loading) return renderLoading();
    if (viewings.data && viewings.data.results && viewings.data.results.length > 0)
      return renderViewings(viewings.data.results);
    return renderEmpty();
  };

  return (
    <>
      <Head>
        <title>{metaTitle}</title>

        <MetaTags
          url={router.asPath}
          title={metaTitle}
          description={META_DESCRIPTION}
          image={{
            url: OgImage.src,
            width: String(650),
            height: String(1200),
          }}
        />
      </Head>

      <PageWrapper containerStyle={{ padding: '30px 0' }}>
        <Heading data-testid="open-viewings-heading">Open Viewings{town}</Heading>
      </PageWrapper>

      {viewings.dates && viewings.dates.length > 0 && (
        <DateCalendar dates={viewings.dates} currentDate={viewings.currentDate} updateDate={updateDate} />
      )}

      {renderContent()}
    </>
  );
};

export default OpenViewings;
